<template>
  <div
    class="comment-item"
    :class="{ 'comment-item--reply': isReply }"
    :data-testid="`comment-item-${comment.id}`"
  >
    <div class="comment-item__avatar">
      <Icon v-if="iq" name="iq_small_icon" data-testid="comment-item-iq-icon" />
      <ExAvatar
        v-else
        size="medium"
        :picture="comment.author && comment.author.avatar"
        :first-name="firstName"
        :last-name="lastName"
      />
    </div>

    <div class="comment-item__main">
      <div class="comment-item__header">
        <span class="comment-item__author" data-testid="comment-item-author">{{ authorName }}</span>
        <span class="comment-item__time" data-testid="comment-item-time">{{ time }}</span>
        <span
          v-if="comment.edited_at"
          class="comment-item__edited"
          data-testid="comment-item-edited"
        >
          Edited
        </span>
      </div>

      <p v-if="!editing" class="comment-item__body" data-testid="comment-item-body">
        {{ comment.body }}
      </p>

      <div v-else class="comment-item__edit">
        <div
          v-if="conflict"
          class="comment-item__conflict"
          role="alert"
          data-testid="comment-edit-conflict"
        >
          <span class="comment-item__conflict-text">
            This comment was changed by someone else. Reload to see the latest version before
            editing.
          </span>
          <button
            type="button"
            class="comment-item__conflict-reload"
            data-testid="comment-edit-reload"
            @click="reload"
          >
            Reload
          </button>
        </div>
        <textarea
          v-model="draft"
          class="comment-item__input"
          rows="2"
          aria-label="Edit comment"
          :disabled="saving"
          data-testid="comment-edit-input"
          @keydown.enter.exact.prevent="save"
        ></textarea>
        <div class="comment-item__edit-buttons">
          <button
            type="button"
            class="comment-item__save"
            :disabled="!canSave"
            data-testid="comment-edit-save"
            @click="save"
          >
            Save
          </button>
          <button
            type="button"
            class="comment-item__cancel"
            data-testid="comment-edit-cancel"
            @click="cancel"
          >
            Cancel
          </button>
        </div>
      </div>

      <CommentActions
        v-if="!editing"
        class="comment-item__actions"
        :is-own="own"
        :is-admin="viewer.isAdmin"
        :can-reply="!isReply"
        :can-resolve="!isReply && !resolved"
        @reply="emit('reply')"
        @resolve="emit('resolve')"
        @edit="startEdit"
        @delete="confirmOpen = true"
      />

      <CommentSeenBy
        :seen-by="comment.seen_by || []"
        :one-to-one="oneToOne"
        :sent-at="comment.created_at"
      />
    </div>

    <ExActionsPopUp
      :value="confirmOpen"
      headline="Delete comment"
      info="This comment will be deleted. Replies to it, if any, will stay."
      action-label="Delete"
      icon="trash_03"
      :destructive="true"
      :delete-popup="true"
      @input="confirmOpen = false"
      @onAction="confirmDelete"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import Icon from "@components/icons/Icon.vue";
import CommentActions from "@components/CommentActions/CommentActions.vue";
import CommentSeenBy from "@components/CommentSeenBy/CommentSeenBy.vue";
import {
  formatCommentTime,
  isIqComment,
  isOwnComment,
} from "@components/ChatterDrawer/composables/commentUtils";
import { useCommentActions } from "@components/ChatterDrawer/composables/useCommentActions";

const props = defineProps({
  comment: { type: Object, required: true },
  viewer: { type: Object, default: () => ({ accessId: null, isAdmin: false }) },
  isReply: { type: Boolean, default: false },
  /** The thread this comment belongs to is resolved. */
  resolved: { type: Boolean, default: false },
  oneToOne: { type: Boolean, default: false },
});

const emit = defineEmits(["reply", "resolve", "updated", "deleted"]);

const actions = useCommentActions();

const editing = ref(false);
const draft = ref("");
const saving = ref(false);
const conflict = ref(false);
const confirmOpen = ref(false);

const iq = computed(() => isIqComment(props.comment));
const own = computed(() => isOwnComment(props.comment, props.viewer));
const authorName = computed(() => (iq.value ? "IQ" : props.comment.author?.name || ""));
const time = computed(() => formatCommentTime(props.comment.created_at));
const nameParts = computed(() => authorName.value.trim().split(/\s+/));
const firstName = computed(() => nameParts.value[0] || "");
const lastName = computed(() => nameParts.value.slice(1).join(" "));

const trimmedDraft = computed(() => draft.value.trim());
const canSave = computed(
  () =>
    !conflict.value &&
    !saving.value &&
    trimmedDraft.value !== "" &&
    trimmedDraft.value !== props.comment.body
);

function startEdit() {
  draft.value = props.comment.body;
  conflict.value = false;
  editing.value = true;
}

function cancel() {
  editing.value = false;
  conflict.value = false;
}

async function save() {
  if (!canSave.value) return;
  saving.value = true;
  const result = await actions.save(props.comment, trimmedDraft.value);
  saving.value = false;
  if (result.ok) {
    editing.value = false;
    emit("updated", result.comment);
  } else if (result.conflict) {
    conflict.value = true;
  }
}

async function reload() {
  const result = await actions.reload(props.comment.id);
  if (!result.ok) return;
  draft.value = result.comment.body;
  conflict.value = false;
  emit("updated", result.comment);
}

async function confirmDelete() {
  confirmOpen.value = false;
  const result = await actions.remove(props.comment.id);
  if (result.ok) emit("deleted", props.comment.id);
}
</script>

<style scoped>
.comment-item {
  display: flex;
  gap: 12px;
  width: 100%;
}

.comment-item--reply {
  padding-left: 44px;
  box-sizing: border-box;
}

.comment-item__avatar {
  display: flex;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.comment-item__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.comment-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
}

.comment-item__author {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.comment-item__time,
.comment-item__edited {
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #667085;
}

.comment-item__body {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-item__edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-item__conflict {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #fda29b;
  border-radius: 8px;
  background: #fef3f2;
}

.comment-item__conflict-text {
  flex: 1;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #b42318;
}

.comment-item__conflict-reload {
  padding: 0;
  border: none;
  background: transparent;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #b42318;
  text-decoration: underline;
  cursor: pointer;
}

.comment-item__input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
  resize: vertical;
  box-sizing: border-box;
}

.comment-item__input:focus {
  outline: none;
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px rgba(127, 86, 217, 0.12);
}

.comment-item__edit-buttons {
  display: flex;
  gap: 8px;
}

.comment-item__save,
.comment-item__cancel {
  padding: 6px 14px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  cursor: pointer;
}

.comment-item__save {
  border: 1px solid #7f56d9;
  background: #7f56d9;
  color: #ffffff;
}

.comment-item__save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.comment-item__cancel {
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
}

.comment-item__save:focus-visible,
.comment-item__cancel:focus-visible,
.comment-item__conflict-reload:focus-visible {
  outline: 2px solid #344054;
  outline-offset: 2px;
}
</style>
